import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {ImagePlus, Upload, Globe, Send, Github, ShieldCheck, Info, Eye, Edit3, ChevronRight, Check, Sparkles, Loader2, X, AlertCircle, CreditCard, CheckCircle2, Clock, RefreshCw, FileSearch, Award, RotateCcw, MessageSquare} from 'lucide-react'
import { lumi } from '@/lib/lumi'

function XLogo({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
    </svg>
  )
}
import Layout from '@/components/Layout'
import { CHAIN_LABELS, toProjectSlug } from '@/lib/types'
import { AUDIT_PACKAGES } from '@/lib/mockData'
import type { EvmChain } from '@/lib/types'
import { useAuth } from '@/hooks/useAuth'
import { useMyProject } from '@/hooks/useProjects'

const C = {
  bgDeep:     '#000000',
  surface:    '#070a0c',
  bgCard:     '#0a0e12',
  turq:       '#1ad6c4',
  turqDim:    'rgba(26,214,196,0.10)',
  borderTurq: 'rgba(26,214,196,0.28)',
  border:     'rgba(255,255,255,0.07)',
  borderMed:  'rgba(255,255,255,0.12)',
  textPrimary:'#ffffff',
  textSecond: '#b4c0cc',
  textMuted:  '#6a7682',
}

const inputBase: React.CSSProperties = {
  background: 'rgba(255,255,255,0.03)',
  border: `1px solid ${C.border}`,
  color: '#ffffff',
  width: '100%',
  borderRadius: 10,
  padding: '11px 14px',
  fontSize: 14,
  outline: 'none',
  transition: 'border-color 0.15s',
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium" style={{ color: C.textSecond }}>
        {label}
      </label>
      {children}
      {hint && (
        <p className="text-xs leading-relaxed" style={{ color: C.textMuted }}>
          {hint}
        </p>
      )}
    </div>
  )
}

// ── Audit Status Panel ─────────────────────────────────────────────────────
function AuditStatusPanel({
  status,
  verdict,
  score,
  summary,
  auditedAt,
  projectId,
  paymentSuccess,
}: {
  status: string | null
  verdict: string | null
  score: number | null
  summary: string | null
  auditedAt: string | null
  projectId: string | null
  paymentSuccess: boolean
}) {
  if (!status) return null

  const isPending   = status === 'pending'
  const isInReview  = status === 'in_review'
  const isCompleted = status === 'completed'
  const isPassed    = verdict === 'passed'

  const statusMeta = isCompleted
    ? isPassed
      ? { label: 'Audit Passed', color: '#22c55e', bg: 'rgba(34,197,94,0.07)', border: 'rgba(34,197,94,0.22)', Icon: CheckCircle2 }
      : { label: 'Audit Failed', color: '#ef4444', bg: 'rgba(239,68,68,0.07)', border: 'rgba(239,68,68,0.22)', Icon: AlertCircle }
    : isInReview
      ? { label: 'Under Review', color: '#1ad6c4', bg: 'rgba(26,214,196,0.07)', border: 'rgba(26,214,196,0.22)', Icon: FileSearch }
      : { label: 'Awaiting Review', color: '#f5c451', bg: 'rgba(245,196,81,0.07)', border: 'rgba(245,196,81,0.22)', Icon: Clock }

  const { label, color, bg, border, Icon } = statusMeta

  return (
    <div
      className="rounded-xl px-5 py-5 mb-8"
      style={{ background: bg, border: `1px solid ${border}` }}
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Icon className="w-5 h-5 flex-shrink-0" style={{ color }} />
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-sm font-semibold" style={{ color }}>{label}</p>
              {isCompleted && score !== null && (
                <span
                  className="text-xs font-bold px-2.5 py-0.5 rounded-full"
                  style={{ background: `${color}18`, color }}
                >
                  Score: {score}/100
                </span>
              )}
              {(isPending || isInReview) && (
                <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: C.textMuted }}>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {paymentSuccess ? 'Confirming payment…' : 'In progress…'}
                </span>
              )}
            </div>
            {summary && (
              <p className="text-xs mt-1 leading-relaxed" style={{ color: C.textSecond }}>
                {summary.length > 160 ? summary.slice(0, 157) + '…' : summary}
              </p>
            )}
            {isCompleted && auditedAt && (
              <p className="text-xs mt-1" style={{ color: C.textMuted }}>
                Audited {new Date(auditedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
              </p>
            )}
          </div>
        </div>
        {isCompleted && projectId && (
          <Link
            to={`/projects/${toProjectSlug(profile?.name ?? projectId)}`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:brightness-110 flex-shrink-0"
            style={{ background: color + '18', color, border: `1px solid ${border}` }}
          >
            <Award className="w-3.5 h-3.5" /> View Report
          </Link>
        )}
      </div>
    </div>
  )
}

function Divider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-4 py-2">
      <div className="flex-1 h-px" style={{ background: C.border }} />
      <span
        className="text-[11px] font-semibold uppercase tracking-widest flex-shrink-0"
        style={{ color: C.turq, letterSpacing: '0.14em' }}
      >
        {label}
      </span>
      <div className="flex-1 h-px" style={{ background: C.border }} />
    </div>
  )
}

export default function Dashboard() {
  const { isAuthenticated, user } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState<'edit' | 'preview'>('edit')

  const userId = user?.userId
  const { profile, loading: profileLoading, refetch } = useMyProject(userId)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  const [searchParams] = useSearchParams()
  const paymentSuccess = searchParams.get('payment') === 'success'

  // File upload state
  const bannerInputRef = useRef<HTMLInputElement>(null)
  const logoInputRef = useRef<HTMLInputElement>(null)
  const [uploadingBanner, setUploadingBanner] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth', { replace: true })
    }
  }, [isAuthenticated, navigate])
  const [form, setForm] = useState({
    name: '',
    symbol: '',
    chain: 'ethereum' as EvmChain,
    contractAddress: '',
    description: '',
    bannerUrl: '',
    logoUrl: '',
    website: '',
    twitter: '',
    telegram: '',
    github: '',
    packageTier: 'starter',
  })

  // Load the current user's own profile into the form (1 login = 1 profile).
  useEffect(() => {
    if (!profile) return
    setForm({
      name: profile.name ?? '',
      symbol: profile.symbol ?? '',
      chain: (profile.chain ?? 'ethereum') as EvmChain,
      contractAddress: profile.contractAddress ?? '',
      description: profile.description ?? '',
      bannerUrl: profile.bannerUrl ?? '',
      logoUrl: profile.logoUrl ?? '',
      website: profile.social?.website ?? '',
      twitter: profile.social?.twitter ?? '',
      telegram: profile.social?.telegram ?? '',
      github: profile.social?.github ?? '',
      packageTier: profile.packageTier ?? 'starter',
    })
  }, [profile])

  // Upload a banner or logo image via the Lumi file SDK.
  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    target: 'bannerUrl' | 'logoUrl',
  ) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-selecting the same file later
    if (!file) return

    setUploadError(null)
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file (JPG, PNG, or WEBP).')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Image is too large. Maximum size is 10MB.')
      return
    }

    const setBusy = target === 'bannerUrl' ? setUploadingBanner : setUploadingLogo
    setBusy(true)
    try {
      const results = await lumi.tools.file.upload([file])
      const result = results?.[0]
      if (result?.fileUrl) {
        setForm((prev) => ({ ...prev, [target]: result.fileUrl as string }))
      } else {
        setUploadError(result?.uploadError || 'Upload failed. Please try again.')
      }
    } catch (error) {
      console.error('Image upload failed:', error)
      setUploadError('Upload failed. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const handleSave = async () => {
    if (!userId) return
    setSaving(true)
    setSaved(false)
    const now = new Date().toISOString()
    const payload = {
      name: form.name,
      symbol: form.symbol,
      chain: form.chain,
      contractAddress: form.contractAddress,
      description: form.description,
      bannerUrl: form.bannerUrl,
      logoUrl: form.logoUrl,
      social: {
        website: form.website,
        twitter: form.twitter,
        telegram: form.telegram,
        github: form.github,
      },
      packageTier: form.packageTier,
      creator: userId,
      updatedAt: now,
    }
    try {
      if (profile?._id) {
        // Edit only your own record (DB enforces CREATOR_ONLY).
        await lumi.entities.project_profiles.update(profile._id, payload)
      } else {
        await lumi.entities.project_profiles.create({ ...payload, createdAt: now })
      }
      await refetch()
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (error) {
      console.error('Failed to save profile:', error)
    } finally {
      setSaving(false)
    }
  }

  const set =
    (key: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }))

  const handlePurchase = async (reauditNote?: string) => {
    setCheckoutError(null)
    if (!profile?._id || !form.name) {
      setCheckoutError('Please save your project profile before purchasing an audit.')
      return
    }
    if (!form.contractAddress) {
      setCheckoutError('Please enter your contract address before purchasing an audit.')
      return
    }
    setCheckoutLoading(true)
    try {
      const pkg = AUDIT_PACKAGES[0]
      const isPromo = typeof pkg.promoPriceUsd === 'number'
      const isReaudit = isAuditComplete
      const token = await lumi.auth.getAccessToken?.()
      const res = await fetch(
        `https://api.lumi.new/v1/functions/p454734998907453440/createCheckout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            projectId: profile._id,
            projectName: form.name,
            isPromo,
            isReaudit,
            reauditNote: reauditNote ?? '',
          }),
        },
      )
      const data = await res.json()
      if (!res.ok || !data.url) {
        throw new Error(data.error || 'Failed to start checkout.')
      }
      // Open Stripe in a new tab safely
      const win = window.open()
      if (win) {
        win.location.href = data.url
      } else {
        window.location.href = data.url
      }
    } catch (err: any) {
      console.error('Checkout error:', err)
      setCheckoutError(err.message || 'Something went wrong. Please try again.')
    } finally {
      setCheckoutLoading(false)
    }
  }

  // Polling after payment: refetch every 3s up to 10 times until audit status updates
  const pollCount = useRef(0)
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current)
      pollTimer.current = null
    }
    pollCount.current = 0
  }, [])

  useEffect(() => {
    if (!paymentSuccess) return
    // Start polling to catch webhook update
    const poll = async () => {
      if (pollCount.current >= 10) { stopPolling(); return }
      pollCount.current += 1
      await refetch()
      // If audit is now in_review we can stop early
      if (profile?.audit?.status === 'in_review') { stopPolling(); return }
      pollTimer.current = setTimeout(poll, 3000)
    }
    pollTimer.current = setTimeout(poll, 2000)
    return () => stopPolling()
  }, [paymentSuccess]) // eslint-disable-line react-hooks/exhaustive-deps

  const auditStatus = profile?.audit?.status ?? null
  const auditVerdict = profile?.audit?.verdict ?? null
  const auditScore = profile?.audit?.score ?? null
  const isAuditActive = auditStatus === 'in_review' || auditStatus === 'pending'
  const isAuditComplete = auditStatus === 'completed'

  // Re-audit state
  const [reauditReason, setReauditReason] = useState('')
  const [showReauditForm, setShowReauditForm] = useState(false)

  const displayName   = form.name   || 'Your Project'
  const displaySymbol = form.symbol || 'SYM'

  return (
    <Layout>
      {/* ── Page header ── */}
      <section style={{ background: C.surface, borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-[920px] mx-auto px-5 py-14">
          <span
            className="text-[11px] font-semibold tracking-widest uppercase mb-3 inline-block"
            style={{ color: C.turq, letterSpacing: '0.14em' }}
          >
            Project Owner
          </span>
          <h1
            className="text-white font-light leading-tight"
            style={{ fontSize: 'clamp(26px, 4vw, 40px)', letterSpacing: '-0.8px' }}
          >
            My Project Profile
          </h1>
          <p className="mt-2 text-sm max-w-xl" style={{ color: C.textMuted }}>
            Set up your public profile, then purchase an audit package to have your
            smart contract reviewed by Mandox Audits.
          </p>

          {/* Tab toggle */}
          <div
            className="inline-flex mt-7 p-1 rounded-xl gap-1"
            style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}` }}
          >
            {[
              { key: 'edit'    as const, label: 'Edit Profile', icon: Edit3 },
              { key: 'preview' as const, label: 'Preview Card', icon: Eye   },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all"
                style={{
                  background: tab === key ? C.turqDim : 'transparent',
                  color:      tab === key ? '#ffffff'  : C.textMuted,
                  border:     tab === key ? `1px solid ${C.borderTurq}` : '1px solid transparent',
                  fontWeight: tab === key ? 500 : 400,
                }}
              >
                <Icon
                  className="w-3.5 h-3.5"
                  style={{ color: tab === key ? C.turq : 'inherit' }}
                />
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section style={{ background: C.bgDeep }} className="py-12">
        <div className="max-w-[920px] mx-auto px-5">

          {/* Payment success banner */}
          {paymentSuccess && (
            <div
              className="flex items-start gap-3 rounded-xl px-4 py-4 mb-6 text-sm"
              style={{ background: 'rgba(26,214,196,0.08)', border: `1px solid ${C.borderTurq}`, color: C.textSecond }}
            >
              <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: C.turq }} />
              <div>
                <p className="font-semibold text-white mb-0.5">Payment received — thank you!</p>
                <p className="text-xs" style={{ color: C.textMuted }}>Your audit is now in the queue. The Mandox Audits team will begin the review shortly. Turnaround: 7–10 business days.</p>
              </div>
            </div>
          )}

          {/* ── Audit Status Panel ── */}
          <AuditStatusPanel
            status={auditStatus}
            verdict={auditVerdict}
            score={auditScore}
            summary={profile?.audit?.summary ?? null}
            auditedAt={profile?.audit?.auditedAt ?? null}
            projectId={profile?._id ?? null}
            paymentSuccess={paymentSuccess}
          />

          {/* Re-audit section — only when completed */}
          {isAuditComplete && (
            <div
              className="rounded-xl px-5 py-5 mb-8"
              style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.borderMed}` }}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                  <RotateCcw className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: C.turq }} />
                  <div>
                    <p className="text-sm font-semibold text-white">Request a Re-audit</p>
                    <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>
                      Made significant changes to your contract? Purchase a new audit to get an updated score and badge.
                    </p>
                  </div>
                </div>
                {!showReauditForm && (
                  <button
                    type="button"
                    onClick={() => setShowReauditForm(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:brightness-110 flex-shrink-0"
                    style={{ background: C.turqDim, color: C.turq, border: `1px solid ${C.borderTurq}` }}
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Start Re-audit
                  </button>
                )}
              </div>

              {showReauditForm && (
                <div className="mt-5 flex flex-col gap-4">
                  <div className="h-px" style={{ background: C.border }} />
                  <div className="flex flex-col gap-1.5">
                    <label className="flex items-center gap-1.5 text-sm font-medium" style={{ color: C.textSecond }}>
                      <MessageSquare className="w-3.5 h-3.5" style={{ color: C.turq }} />
                      What changed? <span className="text-xs font-normal" style={{ color: C.textMuted }}>(optional)</span>
                    </label>
                    <textarea
                      value={reauditReason}
                      onChange={(e) => setReauditReason(e.target.value)}
                      rows={3}
                      placeholder="e.g. Fixed reentrancy vulnerability, updated access control logic, migrated to Solidity 0.8.20…"
                      style={{ ...inputBase, resize: 'none' }}
                    />
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => handlePurchase(reauditReason)}
                      disabled={checkoutLoading || saving || profileLoading}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:brightness-110 active:scale-95 disabled:opacity-60"
                      style={{ background: C.turq, color: '#000' }}
                    >
                      {checkoutLoading
                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
                        : <><CreditCard className="w-4 h-4" /> Purchase Re-audit</>}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowReauditForm(false); setReauditReason('') }}
                      className="text-xs transition-colors hover:text-white"
                      style={{ color: C.textMuted }}
                    >
                      Cancel
                    </button>
                  </div>
                  {checkoutError && (
                    <p className="text-xs" style={{ color: '#fca5a5' }}>{checkoutError}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Notice bar — only when no active/completed audit */}
          {!auditStatus && (
            <div
              className="flex items-start gap-2.5 rounded-xl px-4 py-3 mb-10 text-xs"
              style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}`, color: C.textSecond }}
            >
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: C.turq }} />
              Save your project profile, then click <strong className="text-white">Purchase Audit</strong> to pay securely via Stripe.
            </div>
          )}

          {tab === 'edit' ? (
            <div className="grid lg:grid-cols-[1fr_280px] gap-14">

              {/* ── Left: form ── */}
              <div className="flex flex-col gap-0">

                {/* Branding */}
                <Divider label="Branding" />
                <div className="py-8 flex flex-col gap-6">
                  {/* Hidden file inputs */}
                  <input
                    ref={bannerInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e, 'bannerUrl')}
                  />
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e, 'logoUrl')}
                  />

                  {uploadError && (
                    <div
                      className="flex items-start gap-2 rounded-lg px-3 py-2.5 text-xs"
                      style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)', color: '#fca5a5' }}
                    >
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      {uploadError}
                    </div>
                  )}

                  <Field label="Banner Image" hint="Recommended 1500 × 500 px. JPG, PNG, or WEBP. Max 10MB.">
                    <button
                      type="button"
                      onClick={() => bannerInputRef.current?.click()}
                      disabled={uploadingBanner}
                      className="group relative w-full h-32 rounded-xl flex flex-col items-center justify-center gap-2 overflow-hidden transition-colors hover:bg-white/[0.02] disabled:opacity-70"
                      style={{ border: `1.5px dashed ${C.borderMed}`, color: C.textMuted }}
                    >
                      {form.bannerUrl && !uploadingBanner && (
                        <img
                          src={form.bannerUrl}
                          alt="Banner preview"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      )}
                      {uploadingBanner ? (
                        <>
                          <Loader2 className="w-6 h-6 animate-spin" style={{ color: C.turq }} />
                          <span className="text-xs">Uploading…</span>
                        </>
                      ) : form.bannerUrl ? (
                        <span
                          className="relative z-10 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ background: 'rgba(0,0,0,0.65)', color: '#fff' }}
                        >
                          <Upload className="w-3.5 h-3.5" /> Change banner
                        </span>
                      ) : (
                        <>
                          <ImagePlus className="w-6 h-6" style={{ color: C.turq }} />
                          <span className="text-xs">Click to upload banner</span>
                        </>
                      )}
                    </button>
                  </Field>

                  <Field label="Token Logo" hint="Square image recommended. 512 × 512 px. Max 10MB.">
                    <div className="flex items-center gap-4">
                      <div
                        className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                        style={{ background: 'rgba(255,255,255,0.03)', border: `1.5px dashed ${C.borderMed}` }}
                      >
                        {uploadingLogo ? (
                          <Loader2 className="w-5 h-5 animate-spin" style={{ color: C.turq }} />
                        ) : form.logoUrl ? (
                          <img src={form.logoUrl} alt="Logo preview" className="w-full h-full object-cover" />
                        ) : (
                          <ImagePlus className="w-5 h-5" style={{ color: C.turq }} />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors hover:bg-white/[0.04] disabled:opacity-70"
                        style={{ color: C.textSecond, border: `1px solid ${C.borderMed}` }}
                      >
                        {uploadingLogo
                          ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                          : <><Upload className="w-4 h-4" /> {form.logoUrl ? 'Change Logo' : 'Upload Logo'}</>}
                      </button>
                      {form.logoUrl && !uploadingLogo && (
                        <button
                          type="button"
                          onClick={() => setForm((prev) => ({ ...prev, logoUrl: '' }))}
                          className="inline-flex items-center gap-1.5 text-xs transition-colors hover:text-white"
                          style={{ color: C.textMuted }}
                        >
                          <X className="w-3.5 h-3.5" /> Remove
                        </button>
                      )}
                    </div>
                  </Field>
                </div>

                {/* Project Details */}
                <Divider label="Project Details" />
                <div className="py-8 flex flex-col gap-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Project Name">
                      <input
                        value={form.name}
                        onChange={set('name')}
                        placeholder="e.g. NovaSwap"
                        style={inputBase}
                      />
                    </Field>
                    <Field label="Token Symbol">
                      <input
                        value={form.symbol}
                        onChange={set('symbol')}
                        placeholder="e.g. NOVA"
                        style={inputBase}
                      />
                    </Field>
                    <Field label="EVM Chain">
                      <select value={form.chain} onChange={set('chain')} style={inputBase}>
                        {Object.entries(CHAIN_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Contract Address">
                      <input
                        value={form.contractAddress}
                        onChange={set('contractAddress')}
                        placeholder="0x…"
                        style={{ ...inputBase, fontFamily: 'monospace' }}
                      />
                    </Field>
                  </div>
                  <Field label="Project Description" hint="Describe your project, tokenomics, and goals.">
                    <textarea
                      value={form.description}
                      onChange={set('description')}
                      rows={4}
                      placeholder="Tell investors what your project does…"
                      style={{ ...inputBase, resize: 'none' }}
                    />
                  </Field>
                </div>

                {/* Social & Community */}
                <Divider label="Social & Community" />
                <div className="py-8">
                  <div className="grid sm:grid-cols-2 gap-5">
                    {[
                      { key: 'website'  as const, label: 'Website',    icon: Globe,   ph: 'https://yourproject.io'     },
                      { key: 'twitter'  as const, label: 'Twitter / X', icon: XLogo,   ph: 'https://x.com/yourproject'  },
                      { key: 'telegram' as const, label: 'Telegram',   icon: Send,    ph: 'https://t.me/yourproject'   },
                      { key: 'github'   as const, label: 'GitHub',     icon: Github,  ph: 'https://github.com/yourproject' },
                    ].map(({ key, label, icon: Icon, ph }) => (
                      <Field key={key} label={label}>
                        <div className="relative">
                          <Icon
                            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                            style={{ color: C.turq }}
                          />
                          <input
                            value={form[key]}
                            onChange={set(key)}
                            placeholder={ph}
                            style={{ ...inputBase, paddingLeft: 38 }}
                          />
                        </div>
                      </Field>
                    ))}
                  </div>
                </div>

                {/* Audit Package */}
                <Divider label="Audit Package" />
                <div className="py-8">
                  {(() => {
                    const pkg = AUDIT_PACKAGES[0]
                    const hasPromo = typeof pkg.promoPriceUsd === 'number'
                    return (
                      <div
                        className="rounded-xl p-5 flex flex-col gap-3 relative"
                        style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}` }}
                      >
                        <span
                          className="absolute top-4 right-4 w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ background: C.turq }}
                        >
                          <Check className="w-3 h-3 text-black" />
                        </span>
                        {hasPromo && (
                          <span
                            className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full w-fit"
                            style={{ background: C.turq, color: '#000' }}
                          >
                            <Sparkles className="w-3 h-3" /> Launch promo — first 60 days
                          </span>
                        )}
                        <p className="text-sm font-semibold text-white pr-6">{pkg.name}</p>
                        <div className="flex items-end gap-2 flex-wrap">
                          <span className="text-3xl font-light" style={{ color: C.turq, letterSpacing: '-1px' }}>
                            ${(hasPromo ? pkg.promoPriceUsd! : pkg.priceUsd).toLocaleString()}
                          </span>
                          {hasPromo && (
                            <span className="text-lg font-light mb-0.5 line-through" style={{ color: C.textMuted }}>
                              ${pkg.priceUsd.toLocaleString()}
                            </span>
                          )}
                        </div>
                        <p className="text-xs" style={{ color: C.textMuted }}>{pkg.turnaround}</p>
                      </div>
                    )
                  })()}
                </div>

                {/* Actions */}
                <div className="pt-2 pb-4 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || profileLoading}
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-xl text-sm font-semibold transition-all hover:brightness-110 active:scale-95 disabled:opacity-60"
                    style={{ background: C.turq, color: '#000000' }}
                  >
                    {saving
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                      : saved
                        ? <><Check className="w-4 h-4" /> Saved</>
                        : <><ShieldCheck className="w-4 h-4" /> Save Profile</>}
                  </button>
                  {/* Only show Purchase button when no active or completed audit */}
                  {!isAuditActive && !isAuditComplete && (
                    <button
                      type="button"
                      onClick={() => handlePurchase()}
                      disabled={checkoutLoading || saving || profileLoading}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all hover:brightness-110 active:scale-95 disabled:opacity-60"
                      style={{ background: '#ffffff', color: '#000000', border: 'none' }}
                    >
                      {checkoutLoading
                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
                        : <><CreditCard className="w-4 h-4" /> Purchase Audit</>}
                    </button>
                  )}
                  {isAuditActive && (
                    <span
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium"
                      style={{ background: 'rgba(26,214,196,0.06)', color: C.turq, border: `1px solid ${C.borderTurq}` }}
                    >
                      <Clock className="w-4 h-4" />
                      Audit in progress
                    </span>
                  )}
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-colors hover:bg-white/[0.04]"
                    style={{ color: C.textSecond, border: `1px solid ${C.borderMed}` }}
                  >
                    View Packages <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                  {checkoutError && (
                    <p className="w-full text-xs mt-1" style={{ color: '#fca5a5' }}>{checkoutError}</p>
                  )}
                </div>
              </div>

              {/* ── Right: package summary ── */}
              <div className="hidden lg:block">
                <div className="sticky top-24 flex flex-col gap-0">
                  {/* mini label */}
                  <p
                    className="text-[11px] font-semibold uppercase tracking-widest mb-5"
                    style={{ color: C.turq, letterSpacing: '0.14em' }}
                  >
                    {isAuditComplete ? 'Audit Complete' : isAuditActive ? 'Audit Status' : 'Selected Package'}
                  </p>
                  {isAuditActive ? (
                    // Active audit sidebar
                    <div className="flex flex-col gap-4">
                      <div
                        className="rounded-xl p-4 flex flex-col gap-3"
                        style={{ background: 'rgba(26,214,196,0.06)', border: `1px solid ${C.borderTurq}` }}
                      >
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" style={{ color: C.turq }} />
                          <p className="text-sm font-semibold" style={{ color: C.turq }}>Under Review</p>
                        </div>
                        <p className="text-xs leading-relaxed" style={{ color: C.textMuted }}>
                          The Mandox Audits team is reviewing your smart contract. You'll see results here when complete.
                        </p>
                        <p className="text-xs font-medium" style={{ color: C.textSecond }}>Turnaround: 7–10 business days</p>
                      </div>
                      <div className="h-px" style={{ background: C.border }} />
                      <p className="text-xs" style={{ color: C.textMuted }}>Questions? Reach us via
                        <Link to="/contact" className="ml-1 underline" style={{ color: C.turq }}>Contact</Link>
                      </p>
                    </div>
                  ) : isAuditComplete ? (
                    // Completed audit sidebar
                    <div className="flex flex-col gap-4">
                      <div
                        className="rounded-xl p-4 flex flex-col gap-3"
                        style={{
                          background: auditVerdict === 'passed' ? 'rgba(34,197,94,0.07)' : 'rgba(239,68,68,0.07)',
                          border: `1px solid ${auditVerdict === 'passed' ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
                        }}
                      >
                        <p className="text-sm font-semibold" style={{ color: auditVerdict === 'passed' ? '#22c55e' : '#ef4444' }}>
                          {auditVerdict === 'passed' ? '✓ Audit Passed' : '✗ Audit Failed'}
                        </p>
                        {auditScore !== null && (
                          <p className="text-3xl font-light" style={{ color: auditVerdict === 'passed' ? '#22c55e' : '#ef4444', letterSpacing: '-1px' }}>
                            {auditScore}<span className="text-base ml-1" style={{ color: C.textMuted }}>/100</span>
                          </p>
                        )}
                      </div>
                      <Link
                        to={profile?.name ? `/projects/${toProjectSlug(profile.name)}` : '/projects'}
                        className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-semibold transition-all hover:brightness-110"
                        style={{ background: C.turq, color: '#000' }}
                      >
                        <Award className="w-3.5 h-3.5" /> View Full Report
                      </Link>
                      <button
                        type="button"
                        onClick={() => setShowReauditForm(true)}
                        className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-semibold transition-all hover:bg-white/[0.04]"
                        style={{ color: C.textSecond, border: `1px solid ${C.borderMed}` }}
                      >
                        <RotateCcw className="w-3.5 h-3.5" /> Request Re-audit
                      </button>
                    </div>
                  ) : (
                    // No audit yet: show package
                    (() => {
                      const pkg = AUDIT_PACKAGES[0]
                      if (!pkg) return null
                      const hasPromo = typeof pkg.promoPriceUsd === 'number'
                      return (
                        <div className="flex flex-col gap-4">
                          {hasPromo && (
                            <span
                              className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full w-fit"
                              style={{ background: C.turq, color: '#000' }}
                            >
                              <Sparkles className="w-3 h-3" /> Launch promo
                            </span>
                          )}
                          <div className="flex items-end gap-2 flex-wrap">
                            <span
                              className="text-4xl font-light"
                              style={{ color: C.turq, letterSpacing: '-1.5px' }}
                            >
                              ${(hasPromo ? pkg.promoPriceUsd! : pkg.priceUsd).toLocaleString()}
                            </span>
                            <span className="text-xs mb-1.5" style={{ color: C.textMuted }}>USD</span>
                            {hasPromo && (
                              <span className="text-lg font-light mb-0.5 line-through" style={{ color: C.textMuted }}>
                                ${pkg.priceUsd.toLocaleString()}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="text-base font-medium text-white">{pkg.name}</p>
                            <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>{pkg.turnaround}</p>
                          </div>
                          <div className="h-px w-full" style={{ background: C.border }} />
                          <ul className="flex flex-col gap-2.5">
                            {pkg.features.map((f) => (
                              <li key={f} className="flex items-start gap-2.5 text-xs" style={{ color: C.textSecond }}>
                                <ShieldCheck
                                  className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
                                  style={{ color: C.turq }}
                                />
                                {f}
                              </li>
                            ))}
                          </ul>
                          <button
                            type="button"
                            onClick={() => handlePurchase()}
                            disabled={checkoutLoading || saving || profileLoading}
                            className="mt-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-semibold transition-all hover:brightness-110 disabled:opacity-60"
                            style={{ background: C.turq, color: '#000' }}
                          >
                            {checkoutLoading
                              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Redirecting…</>
                              : <><CreditCard className="w-3.5 h-3.5" /> Purchase Audit</>}
                          </button>
                        </div>
                      )
                    })()
                  )}
                </div>
              </div>
            </div>

          ) : (
            /* ── Preview tab ── */
            <div className="max-w-[480px] mx-auto">
              <p className="text-xs text-center mb-5" style={{ color: C.textMuted }}>
                This is how your project card will appear to viewers in the registry.
              </p>
              <div
                className="rounded-2xl overflow-hidden"
                style={{
                  background: C.bgCard,
                  border: `1px solid ${C.borderTurq}`,
                  boxShadow: `0 0 48px rgba(26,214,196,0.07)`,
                }}
              >
                {/* Banner */}
                <div
                  className="h-24 relative bg-cover bg-center"
                  style={{
                    background: form.bannerUrl ? undefined : 'linear-gradient(135deg, #050810 0%, #071214 50%, #030a0a 100%)',
                    backgroundImage: form.bannerUrl
                      ? `url(${form.bannerUrl})`
                      : `linear-gradient(${C.turq}15 1px, transparent 1px), linear-gradient(90deg, ${C.turq}15 1px, transparent 1px)`,
                    backgroundSize: form.bannerUrl ? 'cover' : '30px 30px',
                  }}
                />
                <div className="px-5 pb-6 -mt-8">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center text-lg font-bold mb-3 overflow-hidden"
                    style={{
                      background: '#0a0e12',
                      color: C.turq,
                      border: `3px solid ${C.bgCard}`,
                    }}
                  >
                    {form.logoUrl
                      ? <img src={form.logoUrl} alt={displayName} className="w-full h-full object-cover" />
                      : displaySymbol.slice(0, 2)}
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base font-semibold text-white">{displayName}</span>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full font-mono"
                      style={{ background: C.turqDim, color: C.turq }}
                    >
                      {displaySymbol}
                    </span>
                  </div>
                  <p className="text-xs mb-2" style={{ color: C.textMuted }}>
                    {CHAIN_LABELS[form.chain]}
                    {form.contractAddress && ` · ${form.contractAddress.slice(0, 8)}…`}
                  </p>
                  {form.description && (
                    <p
                      className="text-xs leading-relaxed line-clamp-2 mb-3"
                      style={{ color: C.textSecond }}
                    >
                      {form.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-xs px-2.5 py-1 rounded-full font-medium"
                      style={{ background: 'rgba(100,116,139,0.12)', color: C.textMuted }}
                    >
                      Unaudited
                    </span>
                    {form.website && (
                      <span className="text-xs" style={{ color: C.turq }}>
                        {form.website.replace('https://', '')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-xs text-center mt-4" style={{ color: C.textMuted }}>
                Complete your profile and purchase an audit to earn the verified badge.
              </p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  )
}
